import React, { createContext, useContext, useReducer } from 'react';

import { Util } from '@finley-technologies/finley-types';

type ContextState = {
  previewIdentifier: string | undefined;
  reportingDate: Util.FullDateStr | undefined;
  fileVersion: number | undefined;
};
type Action = {
  type: 'setPreview';
  payload: ContextState;
};
type Dispatch = (action: Action) => void;
type ReceivableResponseTapePreviewContextValue = {
  state: ContextState;
  dispatch: Dispatch;
};

const ReceivableResponseTapePreviewContext = createContext<null | ReceivableResponseTapePreviewContextValue>(
  null,
);

const receivableResponseTapePreviewReducer = (
  state: ContextState,
  action: Action,
) => {
  switch (action.type) {
    case 'setPreview':
      return {
        previewIdentifier: action.payload.previewIdentifier,
        reportingDate: action.payload.reportingDate,
        fileVersion: action.payload.fileVersion,
      };
    default:
      return state;
  }
};

export const ReceivableResponseTapePreviewContextProvider: React.FC = ({
  children,
}) => {
  const initialState = {
    previewIdentifier: undefined,
    reportingDate: undefined,
    fileVersion: undefined,
  };
  const [state, dispatch] = useReducer(
    receivableResponseTapePreviewReducer,
    initialState,
  );

  const value = { state, dispatch };

  return (
    <ReceivableResponseTapePreviewContext.Provider value={value}>
      {children}
    </ReceivableResponseTapePreviewContext.Provider>
  );
};

/**
 * Receivable response tape preview context hook
 * Stores the latest receivable response tape preview version selected, with the previewIdentifier, reportingDate, and fileVersion
 */
export const useReceivableResponseTapePreviewContext = () => {
  const context = useContext(ReceivableResponseTapePreviewContext);

  if (!context) {
    throw new Error(
      'ReceivableResponseTapePreviewContextProvider must be present to use ReceivableResponseTapePreviewContext',
    );
  }

  return context;
};
